import React from 'react'
import { useTranslation } from 'react-i18next'
import { IdealAnswerItem, AnswerKeySettings, EssayConfig } from '../types'
import RubricQuizTableEditor from '../../OldPortalComponents/RubricQuizTableEditor'
import RubricDescription from '../../OldPortalComponents/RubricDescription'
import BrainlyMathEditor from '@/components/UI/TextAreaNewVersion/BrainlyMathEditor'
import Toggle from '@/components/UI/Toggle'
import yellowBulb from '@/assets/images/yellow-bulb.svg'

interface EssayQuestionProps {
  rubricPositionCheck: string
  idealToggle: boolean
  idealText: string
  idealAnswerItems: IdealAnswerItem[]
  rubricDescIdealAnswerItems: IdealAnswerItem[]
  answerKeySettings: AnswerKeySettings
  rubricDescAnswerKeySettings: AnswerKeySettings
  scoredBy: { value: string; label: string }
  essayConfig?: EssayConfig
  onRubricPositionCheckChange: (value: string) => void
  onIdealToggleChange: (value: boolean) => void
  onIdealTextChange: (value: string) => void
  onAnswerKeyTableChange: (
    value: IdealAnswerItem[],
    rowCount: number,
    columnCount: number,
  ) => void
  onRubricDescAnswerKeyTableChange: (
    value: IdealAnswerItem[],
    rowCount: number,
    columnCount: number,
  ) => void
  onTableHeaderChange: (value: string[]) => void
  onRubricTableHeaderChange: (value: string[]) => void
  onColumnHeaderChange: (value: string[]) => void
  onRubricDescColumnHeaderChange: (value: string[]) => void
}

const EssayQuestion: React.FC<EssayQuestionProps> = ({
  rubricPositionCheck,
  idealToggle,
  idealText,
  idealAnswerItems,
  rubricDescIdealAnswerItems,
  answerKeySettings,
  rubricDescAnswerKeySettings,
  scoredBy,
  essayConfig,
  onRubricPositionCheckChange,
  onIdealToggleChange,
  onIdealTextChange,
  onAnswerKeyTableChange,
  onRubricDescAnswerKeyTableChange,
  onTableHeaderChange,
  onRubricTableHeaderChange,
  onColumnHeaderChange,
  onRubricDescColumnHeaderChange,
}) => {
  const { t } = useTranslation()

  return (
    <>
      {/* Warning Message */}
      <div className="d-flex page-warning">
        <img src={yellowBulb} alt="yellowBulb" className="pr-3" />
        <span>
          {t(
            "Please keep in mind that learners' responses to essay questions are restricted to a maximum of 8000 characters which is approximately 2000 words.",
          )}
        </span>
      </div>

      {/* Scoring Rubric Switch */}
      <div className="d-flex align-items-center justify-content-between px-0 mt-3">
        <h3 className="page-subheading mb-0 font-14">{t('Scoring Rubric ')}</h3>
        <button
          className="btn btn-link font-weight-bold font-14"
          onClick={() =>
            onRubricPositionCheckChange(
              rubricPositionCheck === 'rubric_breakdown'
                ? 'rubric_description'
                : 'rubric_breakdown',
            )
          }
        >
          {rubricPositionCheck === 'rubric_breakdown'
            ? t('Switch to Scoring Rubric with Description')
            : t('Switch to Scoring Rubric with Breakdown')}
        </button>
      </div>

      {/* Rubric Tables */}
      <div className="flex-one px-0 mt-3 rubric-scoring-table">
        {rubricPositionCheck === 'rubric_breakdown' && (
          <RubricQuizTableEditor
            maxCharCount={essayConfig?.max_criteria_characters || 4000}
            onHeaderChange={onTableHeaderChange}
            onColumnHeaderChange={onColumnHeaderChange}
            onAnswerKeyTableChange={onAnswerKeyTableChange}
            tableValues={idealAnswerItems}
            horizontalHeaderValues={answerKeySettings.headers || ['', '', '']}
            verticalHeaderValues={answerKeySettings.column_headers || ['']}
            enableVerticalHeader={
              answerKeySettings.enable_column_header || false
            }
            rowCount={answerKeySettings.row_count || null}
            columnCount={answerKeySettings.column_count || null}
            scoredBy={scoredBy}
            essayGrader
          />
        )}

        {rubricPositionCheck === 'rubric_description' && (
          <RubricDescription
            maxCharCount={essayConfig?.max_criteria_characters || 4000}
            onHeaderChange={onRubricTableHeaderChange}
            onColumnHeaderChange={onRubricDescColumnHeaderChange}
            onAnswerKeyTableChange={onRubricDescAnswerKeyTableChange}
            tableValues={rubricDescIdealAnswerItems}
            horizontalHeaderValues={
              rubricDescAnswerKeySettings.headers || [
                'Score Criteria',
                'Score for Criteria',
                'Description of Scoring Criteria',
              ]
            }
            verticalHeaderValues={
              rubricDescAnswerKeySettings.column_headers || ['']
            }
            enableVerticalHeader={
              rubricDescAnswerKeySettings.enable_column_header || false
            }
            rowCount={rubricDescAnswerKeySettings.row_count || null}
            columnCount={rubricDescAnswerKeySettings.column_count || null}
            scoredBy={scoredBy}
            essayGrader
          />
        )}

        {/* Ideal Answer Section */}
        <div>
          <div className="mt-2 d-flex justify-content-between align-items-center">
            <div>
              <b>{t('Ideal Answer')}</b>
            </div>
            <div>
              <Toggle
                toggled={idealToggle}
                onToggle={() => onIdealToggleChange(!idealToggle)}
                label={t('Show Ideal Answer')}
              />
            </div>
          </div>
          <div className="w-100">
            {idealToggle && (
              <BrainlyMathEditor
                value={idealText}
                onChange={onIdealTextChange}
                mode="inline"
                maxCharCount={essayConfig?.max_essay_characters || 8000}
                placeholder={t('Type ideal answer here')}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default EssayQuestion
